// Typed public errors. Every failure surfaced by the SDK is one of these, so
// callers can branch on `instanceof` (or the discriminant `kind`) without
// parsing strings. Errors never contain secrets; they carry the safe public
// code/message returned by the gateway plus the correlation id for support.
/** Base class for all SDK errors. */
export class GameError extends Error {
    kind;
    status;
    code;
    correlationId;
    constructor(kind, message, opts) {
        super(message, opts?.cause !== undefined ? { cause: opts.cause } : undefined);
        this.name = 'GameError';
        this.kind = kind;
        this.status = opts?.status;
        this.code = opts?.code;
        this.correlationId = opts?.correlationId;
    }
}
/** The caller aborted the request via its AbortSignal. */
export class CancelledError extends GameError {
    constructor(correlationId) {
        super('cancelled', 'request cancelled', correlationId !== undefined ? { correlationId } : undefined);
        this.name = 'CancelledError';
    }
}
/** The request exceeded the configured timeout. */
export class TimeoutError extends GameError {
    constructor() {
        super('timeout', 'request timed out');
        this.name = 'TimeoutError';
    }
}
/** The transport failed (DNS, connection, CORS, offline). */
export class NetworkError extends GameError {
    constructor(cause) {
        super('network', 'network request failed', { cause });
        this.name = 'NetworkError';
    }
}
/** The server response could not be validated against the expected schema. */
export class ValidationError extends GameError {
    constructor(message, cause) {
        super('validation', message, cause !== undefined ? { cause } : undefined);
        this.name = 'ValidationError';
    }
}
/** The session/access token is missing, invalid, or expired (401). */
export class SessionExpiredError extends GameError {
    constructor(code, correlationId) {
        super('unauthorized', 'session expired or unauthorized', {
            status: 401,
            ...(code !== undefined ? { code } : {}),
            ...(correlationId !== undefined ? { correlationId } : {}),
        });
        this.name = 'SessionExpiredError';
    }
}
/** Maps an HTTP status to the corresponding error kind. */
export function kindForStatus(status) {
    switch (status) {
        case 400:
            return 'bad_request';
        case 401:
            return 'unauthorized';
        case 403:
            return 'forbidden';
        case 404:
            return 'not_found';
        case 409:
            return 'conflict';
        case 422:
            return 'unprocessable';
        case 429:
            return 'rate_limited';
        default:
            return status >= 500 ? 'server' : 'unknown';
    }
}
//# sourceMappingURL=errors.js.map